// signinOutcome.js — pure (JSX-free) render logic for the `_signin_outcome`
// object that the detection pipeline stamps onto Defender/AADIP pass-through
// alerts (rule_defender_endpoint_alert_passthrough), plus the portal's
// "Re-check sign-in outcome" inputs.
//
// The object shape (snake_case, identical for the inline pipeline stamp and the
// portal re-check result so both render through one path):
//   { status, password_valid, error_code, ca_status, blocked_by[], request_id,
//     signin_event_time }
//
// status vocabulary: success | valid_password_blocked | wrong_password |
//   lockout_or_disabled | other_failure | pending_signin_not_yet_ingested
//
// THE load-bearing fact (handoff §4): the credential is CONFIRMED COMPROMISED
// whenever password_valid === true OR status === 'success'. Everything else
// (wrong_password / lockout / other_failure) is spray noise — render muted.

export const RECHECK_MIN_WAIT_MS = 30 * 60 * 1000;          // §5: graph_signin ingest ≈20m + 30m mirror cycle
export const MIRROR_HORIZON_MS = 14 * 24 * 60 * 60 * 1000;  // §6.2: SilverM365GraphSignInLogs retains 14 days
export const PENDING_STATUS = 'pending_signin_not_yet_ingested';

function safeParse(s) {
  if (!s || typeof s !== 'string') return null;
  try { return JSON.parse(s); } catch { return null; }
}

// Coerce blocked_by to an array — PowerShell ConvertTo-Json can unwrap a
// single-element array to a scalar.
export function blockedByList(outcome) {
  const b = outcome?.blocked_by;
  if (Array.isArray(b)) return b.filter(Boolean);
  if (b == null || b === '') return [];
  return [b];
}

// True when the credential is confirmed compromised (the primary triage fact).
export function isCompromised(outcome) {
  if (!outcome) return false;
  return outcome.password_valid === true || outcome.status === 'success';
}

export function isPendingOutcome(outcome) {
  return outcome?.status === PENDING_STATUS;
}

// Resolve which outcome to render for an alert:
//   1) a portal re-check result persisted on the row (signinOutcomeJson) wins —
//      it resolved a previously-pending stamp;
//   2) else the inline pipeline stamp on the raw event;
//   3) else a bare persisted status string (rare — json absent).
// `rawEvent` is the parsed raw_event_json (may be null at the list level when the
// caller chose not to parse it).
export function effectiveSigninOutcome(alert, rawEvent) {
  const persisted = safeParse(alert?.signinOutcomeJson);
  if (persisted && persisted.status) return { ...persisted, _source: 'recheck' };

  const inline = rawEvent && typeof rawEvent === 'object' ? rawEvent._signin_outcome : null;
  if (inline && inline.status) return { ...inline, _source: 'inline' };

  if (alert?.signinOutcomeStatus) return { status: alert.signinOutcomeStatus, _source: 'recheck' };
  return null;
}

// Pull the re-check inputs from the raw event. requestId comes from the FIRST
// evidence[].cloudLogonRequestEvidence.requestId — which also scopes the feature
// to AADIP/Defender pass-through alerts (only they carry that evidence kind).
// signinTime is the top-level firstActivityDateTime (the sign-in activity time).
export function extractRecheckInputs(rawEvent) {
  if (!rawEvent || typeof rawEvent !== 'object') return { requestId: null, signinTime: null };
  let requestId = null;
  const ev = Array.isArray(rawEvent.evidence) ? rawEvent.evidence : [];
  for (const e of ev) {
    const r = e?.cloudLogonRequestEvidence?.requestId;
    if (r) { requestId = String(r); break; }
  }
  if (!requestId && rawEvent._signin_outcome?.request_id) requestId = String(rawEvent._signin_outcome.request_id);
  const signinTime = rawEvent.firstActivityDateTime || rawEvent._signin_outcome?.signin_event_time || null;
  return { requestId, signinTime: signinTime ? String(signinTime) : null };
}

// Banner presentation for a resolved/pending outcome.
//   tone: 'critical' (compromised) | 'muted' (spray noise) | 'neutral' (pending)
export function signinOutcomeView(outcome) {
  if (!outcome || !outcome.status) return null;
  const s = outcome.status;
  if (s === PENDING_STATUS) {
    return { kind: 'pending', tone: 'neutral', compromised: false, label: 'Sign-in outcome pending' };
  }
  if (s === 'success') {
    return { kind: 'compromised', tone: 'critical', compromised: true, label: 'SIGN-IN SUCCEEDED — account accessed' };
  }
  if (s === 'valid_password_blocked') {
    // password_basis calibrates the wording (handoff §4): an explicit password
    // step ('confirmed') vs an interactive browser block at CA/MFA with no logged
    // password step ('inferred_post_auth_block'). Compromised either way.
    const label = outcome.password_basis === 'inferred_post_auth_block'
      ? 'PASSWORD COMPROMISED — password accepted, then blocked at CA/MFA'
      : 'PASSWORD COMPROMISED — credential confirmed valid';
    return { kind: 'compromised', tone: 'critical', compromised: true, label };
  }
  if (s === 'blocked_by_ca_password_unconfirmed') {
    // Post-password CA block code but a token was presented (replay) — the
    // password wasn't necessarily entered, so NOT a confirmed compromise.
    return { kind: 'review', tone: 'review', compromised: false, label: 'Blocked by CA — password not confirmed (possible token replay)' };
  }
  if (s === 'wrong_password') {
    return { kind: 'muted', tone: 'muted', compromised: false, label: 'Wrong password (spray noise)' };
  }
  if (s === 'lockout_or_disabled') {
    return { kind: 'muted', tone: 'muted', compromised: false, label: 'Lockout or disabled account' };
  }
  if (s === 'other_failure') {
    return { kind: 'muted', tone: 'muted', compromised: false, label: 'Sign-in failed' };
  }
  return { kind: 'muted', tone: 'muted', compromised: false, label: s };
}

// 30-minute re-check gate + 14-day horizon. Returns the button state.
export function recheckGate(signinTime, now = Date.now()) {
  if (!signinTime) return { eligible: false, beyondHorizon: false, waitMinutes: 0, noTime: true };
  const t = new Date(signinTime).getTime();
  if (Number.isNaN(t)) return { eligible: false, beyondHorizon: false, waitMinutes: 0, noTime: true };
  const age = now - t;
  if (age > MIRROR_HORIZON_MS) return { eligible: false, beyondHorizon: true, waitMinutes: 0 };
  if (age < RECHECK_MIN_WAIT_MS) return { eligible: false, beyondHorizon: false, waitMinutes: Math.ceil((RECHECK_MIN_WAIT_MS - age) / 60000) };
  return { eligible: true, beyondHorizon: false, waitMinutes: 0 };
}
